// ... existing code ...
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const languageOptions = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'zh-TW', label: '繁體中文', short: '繁中' },
  { code: 'ja', label: '日本語', short: 'JP' },
  { code: 'ko', label: '한국어', short: 'KR' },
  { code: 'th', label: 'ภาษาไทย', short: 'TH' },
];

export default function Navbar() {
  const { isDark, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const currentLang = languageOptions.find((l) => l.code === i18n.language) || languageOptions[0];

  // Helper: if not on homepage, prepend / to anchor links so they navigate back home
  const isHome = location.pathname === '/';
  const anchorLink = (anchor: string) => (isHome ? anchor : `/${anchor}`);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close lang dropdown when clicking outside
  useEffect(() => {
    if (!langOpen) return;
    const handleClick = () => setLangOpen(false);
    setTimeout(() => document.addEventListener('click', handleClick, { once: true }), 0);
    return () => document.removeEventListener('click', handleClick);
  }, [langOpen]);

  const navLinks = [
    { label: t('nav.platform'), href: anchorLink('#hero') },
    { label: t('nav.aiAgents'), href: anchorLink('#ai-agents') },
    { label: t('nav.features'), href: anchorLink('#features') },
    { label: t('nav.developers'), href: anchorLink('#developers') },
    { label: t('nav.roadmap'), href: anchorLink('#roadmap') },
  ];

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? isDark
            ? 'bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.08]'
            : 'bg-[#f5f5f5]/90 backdrop-blur-xl border-b border-black/[0.08]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={anchorLink('#hero')} className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 border border-[#00ff88]/20 group-hover:border-[#00ff88]/50 transition-colors">
              <img
                src="https://readdy.ai/api/search-image?query=A%20futuristic%20AI-themed%20crypto%20exchange%20logo%20featuring%20the%20letters%20A%20and%20I%20fused%20into%20a%20single%20elegant%20monogram%20with%20electric%20neon%20green%20gradient%20glow%20and%20neural%20network%20node%20connections%2C%20minimal%20geometric%20design%20on%20a%20deep%20black%20background%20with%20subtle%20digital%20grid%20texture%2C%20professional%20brand%20identity%2C%20high%20resolution%2C%20clean%20sharp%20edges%2C%20no%20additional%20text%20or%20elements%2C%20centered%20composition%2C%20perfect%20for%20dark%20themed%20trading%20platform%20website%20header%20and%20app%20icon&width=256&height=256&seq=logo_zhisu_ai_v1&orientation=squarish"
                alt="ZhiSu"
                className="w-full h-full object-cover"
              />
            </div>
            <span className={`font-semibold text-lg tracking-tight transition-colors ${isDark ? 'text-white' : 'text-[#111]'}`}>
              ZhiSu
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/skills"
              className={`px-4 py-2 text-sm transition-colors rounded-lg whitespace-nowrap ${
                isDark
                  ? 'text-[#999] hover:text-white hover:bg-white/5'
                  : 'text-[#666] hover:text-[#111] hover:bg-black/5'
              }`}
            >
              {t('nav.skills', 'Skills')}
            </Link>
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`px-4 py-2 text-sm transition-colors rounded-lg whitespace-nowrap ${
                  isDark
                    ? 'text-[#999] hover:text-white hover:bg-white/5'
                    : 'text-[#666] hover:text-[#111] hover:bg-black/5'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right: CTA + Theme Toggle + Language + Mobile toggle */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLangOpen(!langOpen);
                }}
                className={`hidden sm:flex h-10 items-center gap-1.5 px-3 rounded-lg border text-xs font-medium transition-all whitespace-nowrap ${
                  isDark
                    ? 'border-white/10 text-[#999] hover:text-white hover:bg-white/5 hover:border-white/20'
                    : 'border-black/10 text-[#666] hover:text-[#111] hover:bg-black/5 hover:border-black/20'
                }`}
                aria-label="Select language"
              >
                <span>{currentLang.short}</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute right-0 top-full mt-2 w-40 rounded-xl border overflow-hidden shadow-lg backdrop-blur-xl z-50 ${
                      isDark
                        ? 'bg-[#0a0a0a]/95 border-white/[0.08]'
                        : 'bg-white/95 border-black/[0.08]'
                    }`}
                  >
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setLangOpen(false);
                        }}
                        className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors text-left ${
                          i18n.language === lang.code
                            ? isDark
                              ? 'text-[#00ff88] bg-white/5'
                              : 'text-[#00ff88] bg-black/5'
                            : isDark
                              ? 'text-[#999] hover:text-white hover:bg-white/5'
                              : 'text-[#666] hover:text-[#111] hover:bg-black/5'
                        }`}
                      >
                        <span className="font-medium">{lang.label}</span>
                        {i18n.language === lang.code && (
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center ml-auto text-[#00ff88]" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`hidden sm:flex w-10 h-10 items-center justify-center rounded-lg border transition-all ${
                isDark
                  ? 'border-white/10 text-[#999] hover:text-white hover:bg-white/5 hover:border-white/20'
                  : 'border-black/10 text-[#666] hover:text-[#111] hover:bg-black/5 hover:border-black/20'
              }`}
              aria-label="Toggle theme"
            >
              <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} w-5 h-5 flex items-center justify-center text-lg`} />
            </button>

            <a
              href={anchorLink('#developers')}
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-[#00ff88]/10 border border-[#00ff88]/30 text-[#00ff88] text-sm font-medium rounded-lg hover:bg-[#00ff88]/20 hover:border-[#00ff88]/50 transition-all whitespace-nowrap"
            >
              <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
              {t('nav.launchApp')}
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isDark
                  ? 'text-white hover:bg-white/5'
                  : 'text-[#111] hover:bg-black/5'
              }`}
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl w-5 h-5 flex items-center justify-center`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden backdrop-blur-xl border-b overflow-hidden ${
              isDark
                ? 'bg-[#050505]/95 border-white/[0.08]'
                : 'bg-[#f5f5f5]/95 border-black/[0.08]'
            }`}
          >
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/skills"
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 rounded-lg transition-colors text-sm ${
                  isDark
                    ? 'text-[#999] hover:text-white hover:bg-white/5'
                    : 'text-[#666] hover:text-[#111] hover:bg-black/5'
                }`}
              >
                {t('nav.skills', 'Skills')}
              </Link>
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-4 py-3 rounded-lg transition-colors text-sm ${
                    isDark
                      ? 'text-[#999] hover:text-white hover:bg-white/5'
                      : 'text-[#666] hover:text-[#111] hover:bg-black/5'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={anchorLink('#developers')}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#00ff88]/10 border border-[#00ff88]/30 text-[#00ff88] text-sm font-medium rounded-lg mt-3"
              >
                <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
                {t('nav.launchApp')}
              </a>

              {/* Mobile Language Switcher */}
              <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
                <p className={`text-xs uppercase tracking-wider mb-2 px-4 ${isDark ? 'text-[#666]' : 'text-[#888]'}`}>
                  Language
                </p>
                <div className="grid grid-cols-2 gap-2 px-4">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setMobileOpen(false);
                      }}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors text-center ${
                        i18n.language === lang.code
                          ? isDark
                            ? 'text-[#00ff88] bg-white/5 border border-[#00ff88]/30'
                            : 'text-[#00ff88] bg-black/5 border border-[#00ff88]/30'
                          : isDark
                            ? 'text-[#999] hover:text-white hover:bg-white/5 border border-transparent'
                            : 'text-[#666] hover:text-[#111] hover:bg-black/5 border border-transparent'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Theme Toggle */}
              <button
                onClick={() => {
                  toggleTheme();
                  setMobileOpen(false);
                }}
                className={`flex items-center gap-2 w-full px-4 py-3 rounded-lg transition-colors text-sm mt-2 ${
                  isDark
                    ? 'text-[#999] hover:text-white hover:bg-white/5'
                    : 'text-[#666] hover:text-[#111] hover:bg-black/5'
                }`}
              >
                <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} w-4 h-4 flex items-center justify-center`} />
                {isDark ? t('nav.lightMode') : t('nav.darkMode')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}